import { useState } from 'react';
import {
  DeleteButton,
  Empty,
  EmployeePicker,
  ErrorBanner,
  Field,
  InlineEdit,
  Loading,
  Modal,
  PageHead,
} from '../components/ui';
import { Segmented } from '../components/data';
import { api } from '../lib/api';
import { formatDate, formatNumber, humanise, toLocalDateString } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { BusinessReview, Employee, ReviewMetric, ReviewSummary } from '../lib/types';
import { GeneratedReview } from './reviews/GeneratedReview';

/**
 * Business Review.
 *
 * Two things live here. GENERATED is the review the app writes for itself —
 * every business unit with its OML score, rocks, issues, to-dos and
 * scorecard, exportable as a PDF. It is the default, because it is right
 * without anybody doing anything to it.
 *
 * SAVED is the older hand-authored review: pick a period, type the metrics
 * and the commentary, agree the actions. It is unchanged and still here
 * because a generated report is a statement of fact and those reviews are a
 * record of a conversation — the second is not derivable from the first.
 */
export function ReviewsPage() {
  const [view, setView] = useState<'generated' | 'saved'>('generated');

  return (
    <>
      <PageHead
        title="Business Review"
        actions={
          <div className="no-print">
            <Segmented
              label="Which review"
              value={view}
              onChange={setView}
              options={[
                { id: 'generated', label: 'Generated' },
                { id: 'saved', label: 'Saved reviews' },
              ]}
            />
          </div>
        }
      />
      {view === 'generated' ? <GeneratedReview /> : <SavedReviews />}
    </>
  );
}

function SavedReviews() {
  const list = useResource<ReviewSummary[]>('/reviews');
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  if (openId !== null) {
    return (
      <ReviewDetail
        id={openId}
        onBack={() => {
          setOpenId(null);
          list.reload();
        }}
      />
    );
  }

  return (
    <>
      <div className="row-between wrap" style={{ marginBottom: 'var(--space-5)' }}>
        <p className="muted small" style={{ maxWidth: '68ch' }}>
          The periodic look back: what the numbers did, what we learned, and what we are going to do
          about it.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          New review
        </button>
      </div>

      {list.error && <ErrorBanner message={list.error} />}
      {list.loading && <Loading />}

      {list.data && list.data.length === 0 && (
        <Empty title="No reviews yet">
          <p>Create one for the current quarter to start tracking against target.</p>
        </Empty>
      )}

      {list.data && list.data.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Review</th>
                  <th>Period</th>
                  <th>Dates</th>
                  <th className="num">Metrics</th>
                  <th className="num">Open actions</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.map((r) => (
                  <tr key={r.ReviewId}>
                    <td className="strong">{r.Title}</td>
                    <td>
                      {r.PeriodLabel ?? humanise(r.PeriodType)}
                    </td>
                    <td className="mono xs">
                      {formatDate(r.PeriodStart)} – {formatDate(r.PeriodEnd)}
                    </td>
                    <td className="num">{r.MetricCount}</td>
                    <td className="num">
                      {r.OpenActionCount > 0 ? (
                        <span className="badge badge-warning">{r.OpenActionCount}</span>
                      ) : (
                        0
                      )}
                    </td>
                    <td>
                      <span className={`badge ${r.Status === 'published' ? 'badge-good' : 'badge-neutral'}`}>
                        {humanise(r.Status)}
                      </span>
                    </td>
                    <td className="actions">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpenId(r.ReviewId)}>
                        Open
                      </button>
                      <DeleteButton
                        what={r.Title}
                        onConfirm={() => void api.del(`/reviews/${r.ReviewId}`).then(list.reload)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && (
        <NewReview
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            list.reload();
            setOpenId(id);
          }}
        />
      )}
    </>
  );
}

function NewReview({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const today = new Date();
  const quarter = Math.floor(today.getMonth() / 3) + 1;
  const [title, setTitle] = useState(`Q${quarter} ${today.getFullYear()} Business Review`);
  const [periodType, setPeriodType] = useState('quarterly');
  const [periodLabel, setPeriodLabel] = useState(`Q${quarter} ${today.getFullYear()}`);
  const [periodStart, setPeriodStart] = useState(
    toLocalDateString(new Date(today.getFullYear(), (quarter - 1) * 3, 1)),
  );
  const [periodEnd, setPeriodEnd] = useState(
    /* Day 0 of the following month is the last day of this one. */
    toLocalDateString(new Date(today.getFullYear(), quarter * 3, 0)),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<{ ReviewId: number }>('/reviews', {
        title,
        periodType,
        periodLabel,
        periodStart,
        periodEnd,
      });
      onCreated(created.ReviewId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New business review"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving || !title.trim()} onClick={() => void submit()}>
            {saving ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <Field label="Title">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <div className="form-grid">
        <Field label="Cadence">
          <select className="select" value={periodType} onChange={(e) => setPeriodType(e.target.value)}>
            {/* Shortest interval to longest, so the two "bi-" labels read
                unambiguously from their neighbours: bi-monthly is every two
                months, bi-annual is twice a year. */}
            <option value="monthly">Monthly</option>
            <option value="bi-monthly">Bi-monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="bi-annual">Bi-annual</option>
            <option value="annual">Annual</option>
          </select>
        </Field>
        <Field label="Period label">
          <input className="input" value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} />
        </Field>
      </div>
      <div className="form-grid">
        <Field label="From">
          <input className="input" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
        </Field>
        <Field label="To">
          <input className="input" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function ReviewDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const review = useResource<BusinessReview>(`/reviews/${id}`);
  const employees = useResource<Employee[]>('/employees?status=active');
  const [error, setError] = useState<string | null>(null);

  if (review.loading) return <Loading />;
  if (review.error) return <ErrorBanner message={review.error} />;
  if (!review.data) return <Empty title="Review not found" />;

  const r = review.data;
  const staff = employees.data ?? [];
  const handle = (err: unknown) => setError(err instanceof Error ? err.message : 'Could not save');

  const onTarget = r.metrics.filter((m) => metricOnTarget(m) === true).length;
  const offTarget = r.metrics.filter((m) => metricOnTarget(m) === false).length;
  const openActions = r.actions.filter((a) => a.Status === 'open' || a.Status === 'in_progress');

  return (
    <>
      <PageHead
        title={r.Title}
        lede={`${r.PeriodLabel ?? humanise(r.PeriodType)} · ${formatDate(r.PeriodStart)} – ${formatDate(r.PeriodEnd)}`}
        actions={
          <>
            <button type="button" className="btn btn-ghost" onClick={onBack}>
              ← All reviews
            </button>
            <select
              className="select"
              style={{ width: 140 }}
              value={r.Status}
              onChange={(e) => void api.put(`/reviews/${id}`, { status: e.target.value }).then(review.reload)}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </>
        }
      />

      {error && <ErrorBanner message={error} />}

      <div className="grid grid-4 mb-4">
        <div className="stat">
          <div className="label">Metrics tracked</div>
          <div className="figure">{r.metrics.length}</div>
          <div className="foot">on the scorecard</div>
        </div>
        <div className="stat">
          <div className="label">On target</div>
          <div className="figure">{onTarget}</div>
          <div className="foot">hit or beat</div>
        </div>
        <div className="stat">
          <div className="label">Off target</div>
          <div className={`figure${offTarget > 0 ? ' accent' : ''}`}>{offTarget}</div>
          <div className="foot">missed</div>
        </div>
        <div className="stat">
          <div className="label">Open actions</div>
          <div className="figure">{openActions.length}</div>
          <div className="foot">still to land</div>
        </div>
      </div>

      <div className="stack">
        <section className="card">
          <div className="card-head">
            <div>
              <span className="eyebrow">Overview</span>
              <h2>Executive summary</h2>
            </div>
          </div>
          <div className="card-body">
            <InlineEdit
              value={r.ExecutiveSummary}
              rows={5}
              placeholder="The period in a few paragraphs — what moved, what did not, and why."
              onSave={(executiveSummary) =>
                void api.put(`/reviews/${id}`, { executiveSummary }).then(review.reload).catch(handle)
              }
            />
          </div>
        </section>

        {/* --- Scorecard --------------------------------------------------- */}
        <section className="card">
          <div className="card-head">
            <div>
              <span className="eyebrow">Numbers</span>
              <h2>Scorecard</h2>
              <p className="sub">Target versus actual. Each number has an owner.</p>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() =>
                void api
                  .post(`/reviews/${id}/metrics`, { name: 'New metric', sortOrder: r.metrics.length * 10 })
                  .then(review.reload)
                  .catch(handle)
              }
            >
              Add metric
            </button>
          </div>

          {r.metrics.length === 0 ? (
            <div className="card-body">
              <p className="muted small">No metrics on this review yet.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th style={{ width: 170 }}>Owner</th>
                    <th className="num" style={{ width: 120 }}>Target</th>
                    <th className="num" style={{ width: 120 }}>Actual</th>
                    <th style={{ width: 110 }}>Unit</th>
                    <th className="center" style={{ width: 110 }}>Result</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {r.metrics.map((m) => {
                    const hit = metricOnTarget(m);
                    return (
                      <tr key={m.MetricId}>
                        <td>
                          <InlineEdit
                            value={m.Name}
                            rows={1}
                            onSave={(name) =>
                              void api.put(`/reviews/metrics/${m.MetricId}`, { name }).then(review.reload).catch(handle)
                            }
                          />
                        </td>
                        <td>
                          <EmployeePicker
                            employees={staff}
                            value={m.OwnerEmployeeId}
                            onChange={(ownerEmployeeId) =>
                              void api
                                .put(`/reviews/metrics/${m.MetricId}`, { ownerEmployeeId })
                                .then(review.reload)
                                .catch(handle)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="input mono right"
                            type="number"
                            step="any"
                            defaultValue={m.TargetValue ?? ''}
                            onBlur={(e) =>
                              void api
                                .put(`/reviews/metrics/${m.MetricId}`, {
                                  targetValue: e.target.value === '' ? null : Number(e.target.value),
                                })
                                .then(review.reload)
                                .catch(handle)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="input mono right"
                            type="number"
                            step="any"
                            defaultValue={m.ActualValue ?? ''}
                            onBlur={(e) =>
                              void api
                                .put(`/reviews/metrics/${m.MetricId}`, {
                                  actualValue: e.target.value === '' ? null : Number(e.target.value),
                                })
                                .then(review.reload)
                                .catch(handle)
                            }
                          />
                          <div className="xs muted mono">{formatNumber(m.ActualValue, m.Unit)}</div>
                        </td>
                        <td>
                          <select
                            className="select"
                            value={m.Unit}
                            onChange={(e) =>
                              void api
                                .put(`/reviews/metrics/${m.MetricId}`, { unit: e.target.value })
                                .then(review.reload)
                                .catch(handle)
                            }
                          >
                            <option value="number">Number</option>
                            <option value="currency">Currency</option>
                            <option value="percent">Percent</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                            <option value="ratio">Ratio</option>
                          </select>
                          <select
                            className="select mt-2"
                            value={m.Direction}
                            onChange={(e) =>
                              void api
                                .put(`/reviews/metrics/${m.MetricId}`, { direction: e.target.value })
                                .then(review.reload)
                                .catch(handle)
                            }
                          >
                            <option value="higher">Higher is better</option>
                            <option value="lower">Lower is better</option>
                          </select>
                        </td>
                        <td className="center">
                          {hit === null ? (
                            <span className="subtle">—</span>
                          ) : (
                            <span className={`badge ${hit ? 'badge-good' : 'badge-critical'}`}>
                              {hit ? 'On target' : 'Missed'}
                            </span>
                          )}
                        </td>
                        <td className="actions">
                          <DeleteButton
                            what={`the metric "${m.Name}"`}
                            onConfirm={() =>
                              void api.del(`/reviews/metrics/${m.MetricId}`).then(review.reload).catch(handle)
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* --- Narrative sections ------------------------------------------ */}
        <section className="card">
          <div className="card-head">
            <div>
              <span className="eyebrow">Narrative</span>
              <h2>Sections</h2>
              <p className="sub">Wins, misses, lessons — whatever this review needs to say.</p>
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() =>
                void api
                  .post(`/reviews/${id}/sections`, { title: 'New section', sortOrder: r.sections.length * 10 })
                  .then(review.reload)
                  .catch(handle)
              }
            >
              Add section
            </button>
          </div>
          <div className="card-body stack">
            {r.sections.length === 0 && <p className="muted small">No sections yet.</p>}
            {r.sections.map((s) => (
              <div key={s.SectionId}>
                <div className="row-between">
                  <div className="grow">
                    <InlineEdit
                      value={s.Title}
                      rows={1}
                      style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}
                      onSave={(title) =>
                        void api.put(`/reviews/sections/${s.SectionId}`, { title }).then(review.reload).catch(handle)
                      }
                    />
                  </div>
                  <DeleteButton
                    what={`the section "${s.Title}"`}
                    label="Remove"
                    onConfirm={() =>
                      void api.del(`/reviews/sections/${s.SectionId}`).then(review.reload).catch(handle)
                    }
                  />
                </div>
                <InlineEdit
                  value={s.Body}
                  rows={3}
                  placeholder="…"
                  onSave={(body) =>
                    void api.put(`/reviews/sections/${s.SectionId}`, { body }).then(review.reload).catch(handle)
                  }
                />
              </div>
            ))}
          </div>
        </section>

        {/* --- Actions ------------------------------------------------------ */}
        <section className="card">
          <div className="card-head">
            <div>
              <span className="eyebrow">Next</span>
              <h2>Actions</h2>
              <p className="sub">A review without owned, dated actions is just a report.</p>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() =>
                void api
                  .post(`/reviews/${id}/actions`, { description: 'New action', sortOrder: r.actions.length * 10 })
                  .then(review.reload)
                  .catch(handle)
              }
            >
              Add action
            </button>
          </div>

          {r.actions.length === 0 ? (
            <div className="card-body">
              <p className="muted small">No actions captured.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th style={{ width: 180 }}>Owner</th>
                    <th style={{ width: 150 }}>Due</th>
                    <th style={{ width: 150 }}>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {r.actions.map((a) => (
                    <tr key={a.ActionId}>
                      <td>
                        <InlineEdit
                          value={a.Description}
                          rows={1}
                          onSave={(description) =>
                            void api
                              .put(`/reviews/actions/${a.ActionId}`, { description })
                              .then(review.reload)
                              .catch(handle)
                          }
                        />
                      </td>
                      <td>
                        <EmployeePicker
                          employees={staff}
                          value={a.OwnerEmployeeId}
                          onChange={(ownerEmployeeId) =>
                            void api
                              .put(`/reviews/actions/${a.ActionId}`, { ownerEmployeeId })
                              .then(review.reload)
                              .catch(handle)
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="date"
                          defaultValue={a.DueDate?.slice(0, 10) ?? ''}
                          onBlur={(e) =>
                            void api
                              .put(`/reviews/actions/${a.ActionId}`, { dueDate: e.target.value || null })
                              .then(review.reload)
                              .catch(handle)
                          }
                        />
                      </td>
                      <td>
                        <select
                          className="select"
                          value={a.Status}
                          onChange={(e) =>
                            void api
                              .put(`/reviews/actions/${a.ActionId}`, { status: e.target.value })
                              .then(review.reload)
                              .catch(handle)
                          }
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In progress</option>
                          <option value="complete">Complete</option>
                          <option value="dropped">Dropped</option>
                        </select>
                      </td>
                      <td className="actions">
                        <DeleteButton
                          what="this action"
                          onConfirm={() =>
                            void api.del(`/reviews/actions/${a.ActionId}`).then(review.reload).catch(handle)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

/** null when there is nothing to compare. Respects Direction, because a lower
 *  churn number beating its target is a win, not a miss. */
function metricOnTarget(m: ReviewMetric): boolean | null {
  if (m.TargetValue === null || m.ActualValue === null) return null;
  return m.Direction === 'lower' ? m.ActualValue <= m.TargetValue : m.ActualValue >= m.TargetValue;
}
